// API client for backend communication

// Dynamic API URL based on environment
const getApiBaseUrl = () => {
  // In local development, prioritize explicit env var first.
  // This avoids accidentally using production URL from public/config.js.
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Auto-detect based on current hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3001/api';
    } else {
      // Allow override via runtime config in non-local environments
      if (window && window.CONFIG && window.CONFIG.API_BASE_URL) {
        return window.CONFIG.API_BASE_URL;
      }
      return `https://${hostname}/api`;
    }
  }

  // Final fallback for non-browser contexts
  if (typeof window !== 'undefined' && window.CONFIG && window.CONFIG.API_BASE_URL) {
    return window.CONFIG.API_BASE_URL;
  }

  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

export { getApiBaseUrl };

function getAuthToken() {
  return localStorage.getItem('authToken') || '';
}

function clearAuthAndNotify() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('currentUserId');
  localStorage.removeItem('currentUserPermissions');
  window.dispatchEvent(new Event('auth:unauthorized'));
}

class ApiClient {
  // Generic API request method
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getAuthToken();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    };

    // Don't set Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);

      // Get response text first to check if it's empty
      const responseText = await response.text();

      if (!response.ok) {
        let errorData = {};
        if (responseText) {
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            // If JSON parsing fails, use the text as error message
            errorData = { error: responseText || `HTTP ${response.status}: ${response.statusText}` };
          }
        }
        const message = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        if (response.status === 401) {
          clearAuthAndNotify();
        }
        throw new Error(message);
      }

      // Handle empty response
      if (!responseText || responseText.trim() === '') {
        console.error('Empty response from', endpoint);
        console.error('Response status:', response.status);
        console.error('Response headers:', response.headers);
        throw new Error(`Empty response from server at ${endpoint}`);
      }

      // Try to parse JSON
      try {
        return JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error(`Invalid JSON response from ${endpoint}`);
      }
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Posts API methods
  async getPosts() {
    return this.request('/posts');
  }

  async getPost(id) {
    return this.request(`/posts/${id}`);
  }

  async addPost(post) {
    return this.request('/posts', {
      method: 'POST',
      body: JSON.stringify(post),
    });
  }

  async updatePost(post) {
    return this.request(`/posts/${post.id}`, {
      method: 'PUT',
      body: JSON.stringify(post),
    });
  }

  async deletePost(postId) {
    return this.request(`/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  async addPostWithImage(postData, imageFile) {
    const formData = new FormData();
    formData.append('postData', JSON.stringify(postData));
    formData.append('image', imageFile);
    return this.request('/posts/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updatePostWithImage(postData, imageFile) {
    const formData = new FormData();
    formData.append('postData', JSON.stringify(postData));
    if (imageFile) {
      formData.append('image', imageFile);
    }
    return this.request(`/posts/${postData.id}/with-image`, {
      method: 'PUT',
      body: formData,
    });
  }

  // Doctor API methods
  async getDoctors() {
    return this.request('/doctors');
  }

  async addDoctor(doctorData, imageFile = null) {
    if (imageFile) {
      return this.addDoctorWithImage(doctorData, imageFile);
    }

    return this.request('/doctors', {
      method: 'POST',
      body: JSON.stringify(doctorData),
    });
  }

  async addDoctorWithImage(doctorData, imageFile) {
    const formData = new FormData();
    formData.append('doctorData', JSON.stringify(doctorData));
    formData.append('image', imageFile);

    return this.request('/doctors/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateDoctor(doctorData, imageFile = null) {
    if (imageFile) {
      return this.updateDoctorWithImage(doctorData, imageFile);
    }

    return this.request(`/doctors/${doctorData.id}`, {
      method: 'PUT',
      body: JSON.stringify(doctorData),
    });
  }

  async updateDoctorWithImage(doctorData, imageFile) {
    const formData = new FormData();
    formData.append('doctorData', JSON.stringify(doctorData));
    formData.append('image', imageFile);

    return this.request(`/doctors/${doctorData.id}/with-image`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteDoctor(doctorId) {
    return this.request(`/doctors/${doctorId}`, {
      method: 'DELETE',
    });
  }

  // Image API methods
  async getImage(imageId) {
    const response = await fetch(`${API_BASE_URL}/images/${imageId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch image');
    }
    return response.blob();
  }

  async getDoctorImage(doctorId) {
    const response = await fetch(`${API_BASE_URL}/images/doctor/${doctorId}`);
    if (!response.ok) {
      return null; // No image found
    }
    return response.blob();
  }

  // Settings & Maintenance
  async getSettings() {
    return this.request('/settings');
  }

  async getMaintenanceStatus() {
    return this.request('/maintenance');
  }

  async setMaintenanceStatus(maintenance) {
    return this.request('/maintenance', {
      method: 'PUT',
      body: JSON.stringify({ maintenance })
    });
  }

  // Authentication
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Users & Permissions
  async getUsers() {
    return this.request('/users');
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getUserPermissions(userId) {
    return this.request(`/users/${userId}/permissions`);
  }

  async updateUserPermissions(userId, permissions) {
    return this.request(`/users/${userId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify(permissions)
    });
  }

  async updateUserPassword(userId, password) {
    return this.request(`/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password })
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }
  // Department API methods
  async getDepartments() {
    return this.request('/departments');
  }

  async getDepartment(id) {
    return this.request(`/departments/${id}`);
  }

  async addDepartment(departmentData) {
    return this.request('/departments', {
      method: 'POST',
      body: JSON.stringify(departmentData),
    });
  }

  async addDepartmentWithImage(departmentData, heroImageFile = null, iconFile = null) {
    const formData = new FormData();
    
    // Add department data as JSON string (backend expects 'departmentData' key)
    formData.append('departmentData', JSON.stringify(departmentData));
    
    if (heroImageFile) {
      formData.append('heroImage', heroImageFile);
    }
    
    if (iconFile) {
      formData.append('iconImage', iconFile);
    }

    return this.request('/departments/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateDepartment(id, departmentData) {
    return this.request(`/departments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(departmentData),
    });
  }

  async updateDepartmentWithImage(id, departmentData, heroImageFile = null, iconFile = null) {
    const formData = new FormData();
    
    // Add department data as JSON string (backend expects 'departmentData' key)
    formData.append('departmentData', JSON.stringify(departmentData));
    
    if (heroImageFile) {
      formData.append('heroImage', heroImageFile);
    }
    
    if (iconFile) {
      formData.append('iconImage', iconFile);
    }

    return this.request(`/departments/${id}/with-image`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteDepartment(id) {
    return this.request(`/departments/${id}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // System status check
  async getSystemStatus() {
    return this.request('/system-status');
  }

  // Board Members API methods
  async getBoardMembers() {
    return this.request('/board-members');
  }

  async getBoardMember(id) {
    return this.request(`/board-members/${id}`);
  }

  async addBoardMember(boardMemberData, imageFile = null) {
    if (imageFile) {
      return this.addBoardMemberWithImage(boardMemberData, imageFile);
    }

    return this.request('/board-members', {
      method: 'POST',
      body: JSON.stringify(boardMemberData),
    });
  }

  async addBoardMemberWithImage(boardMemberData, imageFile) {
    const formData = new FormData();
    formData.append('boardMemberData', JSON.stringify(boardMemberData));
    formData.append('image', imageFile);

    return this.request('/board-members/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateBoardMember(id, boardMemberData, imageFile = null) {
    if (imageFile) {
      return this.updateBoardMemberWithImage(id, boardMemberData, imageFile);
    }

    return this.request(`/board-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(boardMemberData),
    });
  }

  async updateBoardMemberWithImage(id, boardMemberData, imageFile) {
    const formData = new FormData();
    formData.append('boardMemberData', JSON.stringify(boardMemberData));
    formData.append('image', imageFile);

    return this.request(`/board-members/${id}/with-image`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteBoardMember(id) {
    return this.request(`/board-members/${id}`, {
      method: 'DELETE',
    });
  }

  async getCEO() {
    return this.request('/board-members/ceo');
  }

  // Team Members API methods
  async getTeamMembers() {
    return this.request('/team-members');
  }

  async getTeamMember(id) {
    return this.request(`/team-members/${id}`);
  }

  async addTeamMember(teamMemberData, imageFile = null) {
    if (imageFile) {
      return this.addTeamMemberWithImage(teamMemberData, imageFile);
    }

    return this.request('/team-members', {
      method: 'POST',
      body: JSON.stringify(teamMemberData),
    });
  }

  async addTeamMemberWithImage(teamMemberData, imageFile) {
    const formData = new FormData();
    formData.append('teamMemberData', JSON.stringify(teamMemberData));
    formData.append('image', imageFile);

    return this.request('/team-members/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateTeamMember(id, teamMemberData, imageFile = null) {
    if (imageFile) {
      return this.updateTeamMemberWithImage(id, teamMemberData, imageFile);
    }

    return this.request(`/team-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(teamMemberData),
    });
  }

  async updateTeamMemberWithImage(id, teamMemberData, imageFile) {
    const formData = new FormData();
    formData.append('teamMemberData', JSON.stringify(teamMemberData));
    if (imageFile) {
      formData.append('image', imageFile);
    }

    return this.request(`/team-members/${id}/with-image`, {
      method: 'PUT',
      body: formData,
    });
  }

  async deleteTeamMember(id) {
    return this.request(`/team-members/${id}`, {
      method: 'DELETE',
    });
  }

  // Jobs API methods
  async getJobs() {
    return this.request('/jobs');
  }

  async getJob(id) {
    return this.request(`/jobs/${id}`);
  }

  async addJob(jobData) {
    return this.request('/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
  }

  async updateJob(id, jobData) {
    return this.request(`/jobs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(jobData),
    });
  }

  async deleteJob(id) {
    return this.request(`/jobs/${id}`, {
      method: 'DELETE',
    });
  }

  // Job Applications API methods
  async getJobApplications() {
    return this.request('/job-applications');
  }

  async getJobApplication(id) {
    return this.request(`/job-applications/${id}`);
  }

  async addJobApplication(applicationData) {
    return this.request('/job-applications', {
      method: 'POST',
      body: JSON.stringify(applicationData),
    });
  }

  async addJobApplicationWithFile(applicationData, resumeFile) {
    const formData = new FormData();
    formData.append('applicationData', JSON.stringify(applicationData));
    if (resumeFile) {
      formData.append('resume', resumeFile);
    }
    return this.request('/job-applications/with-file', {
      method: 'POST',
      body: formData,
    });
  }

  async updateJobApplication(id, applicationData) {
    return this.request(`/job-applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(applicationData),
    });
  }

  async deleteJobApplication(id) {
    return this.request(`/job-applications/${id}`, {
      method: 'DELETE',
    });
  }

  async updateJobApplicationStatus(id, status) {
    return this.request(`/job-applications/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async downloadJobApplicationResume(applicationId) {
    const id = encodeURIComponent(String(applicationId));
    const baseUrl = getApiBaseUrl().replace(/\/$/, '');
    const urls = [
      `${baseUrl}/job-applications/${id}?download=resume`,
      `${baseUrl}/resumes/${id}`,
      `${baseUrl}/job-applications/resume/${id}`,
      `${baseUrl}/job-applications/${id}/resume`,
    ];

    let lastError = 'Could not download resume';

    const token = getAuthToken();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    for (const url of urls) {
      try {
        const response = await fetch(url, { headers: authHeaders });

        if (!response.ok) {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorData = await response.json();
            lastError = errorData.message || errorData.error || lastError;
          } else {
            lastError = `HTTP ${response.status}: ${response.statusText}`;
          }
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errorData = await response.json();
          lastError = errorData.message || errorData.error || lastError;
          continue;
        }

        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition') || '';
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
        const filename = filenameMatch?.[1] || 'resume.pdf';

        return { blob, filename };
      } catch (error) {
        lastError = error.message || lastError;
      }
    }

    throw new Error(lastError);
  }

  // Services API methods
  async getServices() {
    return this.request('/services');
  }

  async getService(id) {
    return this.request(`/services/${id}`);
  }

  async addService(serviceData, imageFile = null) {
    if (imageFile) {
      return this.addServiceWithImage(serviceData, imageFile);
    }

    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async addServiceWithImage(serviceData, imageFile) {
    const formData = new FormData();
    formData.append('serviceData', JSON.stringify(serviceData));
    formData.append('image', imageFile);

    return this.request('/services/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateService(id, serviceData, imageFile = null) {
    if (imageFile) {
      return this.updateServiceWithImage(id, serviceData, imageFile);
    }

    return this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  async updateServiceWithImage(id, serviceData, imageFile) {
    const formData = new FormData();
    formData.append('serviceData', JSON.stringify(serviceData));
    if (imageFile) {
      formData.append('image', imageFile);
    }

    return this.request(`/services/${id}/with-image`, {
      method: 'PUT',
      body: formData,
    });
  }

  async deleteService(id) {
    return this.request(`/services/${id}`, {
      method: 'DELETE',
    });
  }

  // Service Pages CMS API methods
  async getServicePages() {
    return this.request('/service-pages');
  }

  async getServicePage(slug) {
    return this.request(`/service-pages/${slug}`);
  }

  async saveServicePage(slug, payload) {
    return this.request(`/service-pages/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async saveServicePageWithImage(slug, pageData, heroImageFile) {
    const formData = new FormData();
    formData.append('pageData', JSON.stringify(pageData));
    if (heroImageFile) {
      formData.append('heroImage', heroImageFile);
    }
    return this.request(`/service-pages/${slug}/with-image`, {
      method: 'PUT',
      body: formData,
    });
  }

  // Hospital Info API methods
  async getHospitalInfo() {
    return this.request('/hospital-info');
  }

  async updateHospitalInfo(hospitalInfoData) {
    return this.request('/hospital-info', {
      method: 'PUT',
      body: JSON.stringify(hospitalInfoData),
    });
  }

  async getHospitalInfoSetting(key) {
    return this.request(`/hospital-info/${key}`);
  }

  async updateHospitalInfoSetting(key, value) {
    return this.request(`/hospital-info/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  // Contact form API method
  async submitContactForm(formData) {
    return this.request('/contact', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  // Contact Messages (admin) API methods
  async getContactSubmissions() {
    return this.request('/contact-submissions');
  }

  async updateContactSubmissionStatus(id, status) {
    return this.request(`/contact-submissions/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteContactSubmission(id) {
    return this.request(`/contact-submissions/${id}`, {
      method: 'DELETE',
    });
  }

  // Hero Slider API methods
  async getHeroSlides() {
    return this.request('/hero-slides');
  }

  async addHeroSlide(slideData, imageFile = null) {
    if (imageFile) {
      return this.addHeroSlideWithImage(slideData, imageFile);
    }
    return this.request('/hero-slides', {
      method: 'POST',
      body: JSON.stringify(slideData),
    });
  }

  async addHeroSlideWithImage(slideData, imageFile) {
    const formData = new FormData();
    formData.append('slideData', JSON.stringify(slideData));
    formData.append('image', imageFile);
    return this.request('/hero-slides/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateHeroSlide(id, slideData, imageFile = null) {
    if (imageFile) {
      return this.updateHeroSlideWithImage(id, slideData, imageFile);
    }
    return this.request(`/hero-slides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(slideData),
    });
  }

  async updateHeroSlideWithImage(id, slideData, imageFile) {
    const formData = new FormData();
    formData.append('slideData', JSON.stringify(slideData));
    if (imageFile) {
      formData.append('image', imageFile);
    }
    return this.request(`/hero-slides/${id}/with-image`, {
      method: 'PUT',
      body: formData,
    });
  }

  async deleteHeroSlide(id) {
    return this.request(`/hero-slides/${id}`, {
      method: 'DELETE',
    });
  }

  // Homepage Featured Services API methods
  async getFeaturedServices() {
    return this.request('/featured-services');
  }

  async addFeaturedService(serviceData, imageFile = null) {
    if (imageFile) {
      return this.addFeaturedServiceWithImage(serviceData, imageFile);
    }
    return this.request('/featured-services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async addFeaturedServiceWithImage(serviceData, imageFile) {
    const formData = new FormData();
    formData.append('serviceData', JSON.stringify(serviceData));
    formData.append('image', imageFile);
    return this.request('/featured-services/with-image', {
      method: 'POST',
      body: formData,
    });
  }

  async updateFeaturedService(id, serviceData, imageFile = null) {
    if (imageFile) {
      return this.updateFeaturedServiceWithImage(id, serviceData, imageFile);
    }
    return this.request(`/featured-services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  async updateFeaturedServiceWithImage(id, serviceData, imageFile) {
    const formData = new FormData();
    formData.append('serviceData', JSON.stringify(serviceData));
    if (imageFile) {
      formData.append('image', imageFile);
    }
    return this.request(`/featured-services/${id}/with-image`, {
      method: 'PUT',
      body: formData,
    });
  }

  async deleteFeaturedService(id) {
    return this.request(`/featured-services/${id}`, {
      method: 'DELETE',
    });
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;